import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fetchUnreadCount } from '../../features/notifications/notification-service';

interface NotificationContextType {
    unreadCount: number;
    isLoadingCount: boolean;
    updateUnreadCount: () => Promise<void>;
    decrementUnreadCount: (amount?: number) => void;
    setUnreadCount: (count: number) => void;
}

interface UnreadCountResponse {
    count: number;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [isLoadingCount, setIsLoadingCount] = useState<boolean>(false);

    const updateUnreadCount = useCallback(async () => {
        try {
            setIsLoadingCount(true);
            const response: UnreadCountResponse = await fetchUnreadCount();
            setUnreadCount(response.count || 0);
        } catch (error) {
            console.error('Erro ao buscar contador de notificações:', error);
            setUnreadCount(0);
        } finally {
            setIsLoadingCount(false);
        }
    }, []);

    const decrementUnreadCount = useCallback((amount: number = 1) => {
        setUnreadCount(prev => Math.max(0, prev - amount));
    }, []);

    // Atualizar contador a cada 30 segundos
    useEffect(() => {
        updateUnreadCount();
        const interval = setInterval(updateUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [updateUnreadCount]);

    const value = {
        unreadCount,
        isLoadingCount,
        updateUnreadCount,
        decrementUnreadCount,
        setUnreadCount
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotificationContext = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotificationContext deve ser usado dentro de um NotificationProvider');
    }
    return context;
};